var $ = require('jquery');
var _ = require('underscore');
var TorsoService = require('backbone-torso/modules/ServiceCell');
var WizardNavView = require('./WizardNavView');
var WizardButtonsView = require('./WizardButtonsView');
var wizardPreregExtService = require('./../urls').wizardPreregExtService;
var preRegNavTemplate = require('./preRegNavTemplate.hbs');
var launcherAppInterface = require('./../launcherApplicationInterface');
var wizardUtils = require('./wizardUtils');

/**
 * This is a container that fetches wizard data so it is available to the two views that need it.  It is meant as a
 * temporary solution for embedding torso pages in the existing VCC wizard. Once all prereg functionality is ported
 * to Torso, wizard should be replaced with a 'Hub and Spoke' model.
 *
 * All interactions with the wizard should be managed by this class.
 * - WizardButtonsView and WizardNavView should be considered internal to this class and not be accessed directly except to attach them in the perspective.
 * - Wizard configuration can be accessed via this.getFetchWizardConfigDeferred().
 * - To refresh the wizard config from the server call this.fetchWizardConfig() (this is called automatically when this is initialized).
 *
 * [Simple case] Add a new wizard step (This should be all that is needed unless it is complicated):
 * 1) Use the WizardPerspectiveBehavior to configure the wizard to use.
 * 2) Use the wizardPerspectiveTemplate and set the content view on the behavior when available.
 *
 * [Next level of complexity] Get an instance of this class (generally only needed by child views that need to affect the wizard... for example to change the questionnaire name):
 * A single WizardWidget should be used per initial options (appointment external id and patient vecna id).  Instances
 * of the wizard widget are managed by WizardWidget.getWizardWidget({ patientVecnaId: 'string', appointmentExternalId: 'string', regDate: 'string' }).
 * It can be called multiple times and will return the same widget for a given patient vecna id, appointment external id, and regDate.
 *
 * [Full complexity] When in use these are the steps to using the WizardWidget (All handled by WizardPerspectiveBehavior):
 * 1) Get the WizardWidget to use (automatically added by the WizardPerspectiveBehavior or via
 *    WizardWidget.getWizardWidget({ patientVecnaId: 'string', appointmentExternalId: 'string', regDate: 'string' })).
 * 2) When the page to display changes (currentPageName, currentChildPageName or questionnaireName) then call this.navigateToWizardPage(pageContext).
 * 3) Render the wizard nav and wizard buttons (this is handled by the WizardPerspectiveBehavior assuming the wizard-nav and wizard-buttons injection points exist).
 * -- wizardPerspectiveTemplate can be used for the general wizard layout.
 *
 * Note: when forms are added to this they should follow the questionnaire paradigm for sub-wizards.  I.e. track the form name
 * as a separate page context property.
 *
 * @class WizardWidget
 * @constructor
 * @param options {Object} Options to configure the views
 *   @param options.appointmentExternalId {String} the external id of the appointment being displayed
 *   @param options.regDate {String} the date of the appointments being displayed
 *   @param options.patientVecnaId {String} the vecna id of the patient being displayed
 *   @param [options.navViewTemplate] {Handlebars.Template} template to use for the nav view (specific to the wizard being displayed)
 */
var WizardWidget = TorsoService.extend({
  /**
   * Note: this property exists on both the class object (WizardWidget.NAVIGATION_DIRECTIONS) and instance (new WizardWidget(...).NAVIGATION_DIRECTIONS).
   * @property NAVIGATION_DIRECTIONS {Object} convenience reference of WizardButtonsView.NAVIGATION_DIRECTIONS.
   */
  NAVIGATION_DIRECTIONS: WizardButtonsView.NAVIGATION_DIRECTIONS,

  /**
   * Note: this property exists on both the class object (WizardWidget.PAGE_NAMES) and instance (new WizardWidget(...).PAGE_NAMES).
   * @property PAGE_NAMES {Object} convenience reference of WizardButtonsView.PAGE_NAMES.
   */
  PAGE_NAMES: WizardButtonsView.PAGE_NAMES,

  /**
   * Sets the current page of the wizard to the page described by the context.
   * @method navigateToWizardPage
   * @param pageContext {Object}
   *   @param pageContext.currentPageName {String} the name of the top-level wizard step.
   *   @param [pageContext.currentChildPageName] {String} the name of the sub-wizard step.
   *   @param [pageContext.continueAllowed] {Boolean} whether the user can continue on this page.
   *   @param [pageContext.questionnaireName] {String} the name of the questionnaire being displayed.
   *   @param pageContext.lastNavigationDirection {String} Back or Forward depending on whether the navigation to this
   *                                                       page was due to a back or forward action.
   */
  navigateToWizardPage: function(pageContext) {
    this.set(pageContext);
  },

  /**
   * Fetches the wizard configuration from the server and saves it locally to this service.
   * Note this function is bound to an instance of WizardWidget so it can be safely passed as a functional reference
   * without context binding.
   * @method fetchWizardConfig
   * @return {jQuery.Deferred} that resolves/rejects with the ajax request to get the wizard configuration for the appointment.
   */
  fetchWizardConfig: function() {
    this._fetchWizardConfigDeferred = this._fetchWizardData('', 'wizardData');
    return this._fetchWizardConfigDeferred;
  },

  /**
   * Get the deferred object to use when running code that requires that the wizard configuration has been retrieved.
   * @method getFetchWizardConfigDeferred
   * @return {jQuery.Deferred} that resolves/rejects with the ajax request to get the wizard configuration for the appointment.
   */
  getFetchWizardConfigDeferred: function() {
    return this._fetchWizardConfigDeferred;
  },

  /**
   * Fetches the sub wizard configuration from the server and saves a deferred for it.
   * @method fetchQuestionnaireWizardConfig
   * @return {jQuery.Deferred} that resolves/rejects with the ajax request to get the sub wizard configuration for the appointment.
   */
  fetchQuestionnaireWizardConfig: function() {
    this._fetchQuestionnaireWizardConfigDeferred = this._fetchWizardData('/surveys', 'questionnaireWizardData');
    return this._fetchQuestionnaireWizardConfigDeferred;
  },

  /**
   * Fetches the sub wizard configuration from the server and saves a deferred for it.
   * @method fetchFormsWizardConfig
   * @return {jQuery.Deferred} that resolves/rejects with the ajax request to get the sub wizard configuration for the appointment.
   */
  fetchFormsWizardConfig: function() {
    this._fetchFormsWizardConfigDeferred = this._fetchWizardData('/forms', 'formWizardData');
    return this._fetchFormsWizardConfigDeferred;
  },

  /**
   * Fetches the sub wizard configuration from the server and returns the deferred promise.
   * @method fetchFormsWizardConfig
   * @param urlSuffix the to pass to the backend to identify the sub wizard to retrieve.  Leave empty to fetch the root wizard config.
   * @param wizardDataPropertyName the name of the property to use to save the response.
   * @return {jQuery.Deferred} that resolves/rejects with the ajax request to get the sub wizard configuration for the appointment.
   */
  _fetchWizardData: function(urlSuffix, wizardDataPropertyName) {
    return $.ajax({
      contentType: 'application/json',
      type: 'POST',
      dataType: 'json',
      url: wizardPreregExtService + urlSuffix,
      data: JSON.stringify({
        appointmentExternalIds: wizardUtils.getSelectedPreRegWizardAppointmentExternalIds(this._appointmentExternalId, this._regDate),
        isOnsite: launcherAppInterface.isAtHospital()
      })
    }).then(function(wizardData) {
      this.set(wizardDataPropertyName, wizardData);
      return wizardData;
    }.bind(this));
  },

  /**
   * Navigates to the next wizard url.
   * @method navigateToNextStep
   */
  navigateToNextStep: function() {
    // use deferred so that we know the _nextPageUrl has been set by the config parsing.
    this.getFetchWizardConfigDeferred().then(function() {
      this.wizardButtonsView.continueToNextPage();
    }.bind(this));
  },

  /**
   * Navigates to the previous wizard url.
   * @method navigateToPreviousStep
   */
  navigateToPreviousStep: function() {
    // use deferred so that we know the _nextPageUrl has been set by the config parsing.
    this.getFetchWizardConfigDeferred().then(function() {
      this.wizardButtonsView.backToPreviousPage();
    }.bind(this));
  },

  /**
   * Continue the wizard navigation in the direction that entered this step.
   */
  continueNavigation: function() {
    if (this.get('lastNavigationDirection') === WizardButtonsView.NAVIGATION_DIRECTIONS.BACK) {
      this.navigateToPreviousStep();
    } else {
      this.navigateToNextStep();
    }
  },

  /**
   * Marks the current step completed.
   * @method markStepCompleted
   * @return a promise that resolves once the step has been marked complete.
   */
  markStepCompleted: function() {
    return this.wizardButtonsView.markStepCompleted();
  },

  /**
   * Marks the current wizard completed.
   * Note this function is bound to an instance of WizardWidget so it can be safely passed as a functional reference
   * without context binding.
   * @method completeWizardWorkflow
   * @return a promise that resolves once the wizard has been marked complete.
   */
  completeWizardWorkflow: function() {
    return this.wizardButtonsView.completeWizardWorkflow();
  },

  /**
   * Set viewState continueAllowed, determine whether to allow user to continue to next step or not
   * @method setContinueAllowed
   * @param continueAllowed {Boolean} if true, allow user to navigate to next step, otherwise show error message
   */
  setContinueAllowed: function (continueAllowed) {
    this.wizardButtonsView.setContinueAllowed(continueAllowed);
  },

  /**
   * Set a loading message to display.  Also disables the primary navigation buttons until this method is called with
   * a falsey argument.
   * @method setLoadingMessage
   * @param [loadingMessage=false] {String|Boolean} If truthy, then it is displayed and the primary buttons are disabled.
   *                                                If falsey, then no message is displayed and the primary buttons are enabled.
   */
  setLoadingMessage: function(loadingMessage) {
    this.wizardButtonsView.setLoadingMessage(loadingMessage);
  },

  /**
   * Clears the loading message and re-enables the primary buttons.
   * Note this function is bound to an instance of WizardWidget so it can be safely passed as a functional reference
   * without context binding.
   * @method clearLoadingMessage
   */
  clearLoadingMessage: function() {
    this.setLoadingMessage('');
  },

  /**
   * Shows the print button in the wizard action button.
   * The print button will trigger a 'print' event on this wizardWidget instance.
   * @method showPrintButton
   */
  showPrintButton: function() {
    this.wizardButtonsView.showPrintButton();
  },

  /**
   * Hides the print button in the wizard action button.
   * The print button will trigger a 'print' event on this wizardWidget instance.
   * @method hidePrintButton
   */
  hidePrintButton: function() {
    this.wizardButtonsView.hidePrintButton();
  },

  /**
   * Sets the action to take when navigating to the next questionnaire page for the given questionnaire name.
   * @method setNextQuestionnairePageAction
   * @param questionnaireName {String} the name of the questionnaire to register the action for.
   * @param nextAction {Function} A function that returns a promise.  The function is run when the wizard widget
   *                              advances to the next step for the given questionnaire and any wizard specific actions
   *                              (including actual navigation) will only run if the action resolves.
   *                              If the returned promise is rejected the remaining wizard actions are skipped.
   */
  setNextQuestionnairePageAction: function(questionnaireName, nextAction) {
    this.wizardButtonsView.setNextQuestionnairePageAction(questionnaireName, nextAction);
  },

  /**
   * Sets the action to take when navigating to the next questionnaire page for the given questionnaire name.
   * @method setPreviousQuestionnairePageAction
   * @param questionnaireName {String} the name of the questionnaire to register the action for.
   * @param previousAction {Function} A function that returns a promise.  The function is run when the wizard widget
   *                                  advances to the previous step for the given questionnaire and any wizard specific actions
   *                                  (including actual navigation) will only run if the action resolves.
   *                                  If the returned promise is rejected the remaining wizard actions are skipped.
   */
  setPreviousQuestionnairePageAction: function(questionnaireName, previousAction) {
    this.wizardButtonsView.setPreviousQuestionnairePageAction(questionnaireName, previousAction);
  },

  /**
   * Set viewState paymentAllowed, determine whether to show the make payment button or not.
   * @method setPaymentAllowed
   * @param paymentAllowed {Boolean} if true, show the make payment button, otherwise hide the make payment button
   */
  setPaymentAllowed: function (paymentAllowed) {
    this.wizardButtonsView.setPaymentAllowed(paymentAllowed);
  },

  /**
   * @method initialize
   * @param options {Object} Options to configure the views
   *   @param options.appointmentExternalId {String} the external id of the appointment being displayed
   *   @param options.regDate {String} the date of the appointments being displayed
   *   @param options.patientVecnaId {String} the vecna id of the patient being displayed
   *   @param [options.navViewTemplate] {Handlebars.Template} template to use for the nav view (specific to the wizard being displayed)
   * @override
   */
  initialize: function(options) {
    _.bindAll(this, '_updateWizardData', 'fetchWizardConfig', 'fetchQuestionnaireWizardConfig', 'clearLoadingMessage', 'completeWizardWorkflow');

    this._patientVecnaId = options.patientVecnaId;
    this._appointmentExternalId = options.appointmentExternalId;
    this._regDate = options.regDate;
    var wizardId = this._getWizardId();

    this.wizardNavView = new WizardNavView({
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._appointmentExternalId,
      regDate: this._regDate,
      template: options.navViewTemplate
    });

    this.wizardButtonsView = new WizardButtonsView(wizardId);

    this.listenTo(this.wizardButtonsView, 'print', function() {
      this.trigger('print');
    });

    this.listenTo(this.wizardButtonsView, 'sign-form', function(agreed) {
      this.trigger('sign-form', agreed);
    });

    this.listenTo(this.wizardButtonsView, 'change:navigationDirection', function(navigationDirection) {
      this.set('lastNavigationDirection', navigationDirection);
    });

    // There are 3 steps to using the wizard.
    // Note these are filtered through change events so that the functions only run when something changes - this makes
    // the set methods cheaper and reduces the worry about setting them often.
    //   The first step is fetching a new wizard config when the current page or child page name changes.  This assures the
    //   data is up to date when navigating to a new page (since the previous page may change the configuration on the server).
    //   Note: this is triggered by calling this.navigateToWizardPage() if the currentPageName or currentChildPageName change.
    //   Note2: This is explicitly ignoring the change:questionnaireName so it only refetches when entering the questionnaire node.
    //     Add it in if the wizard config changes and needs to be refetched between surveys.
    this.on('change:currentPageName change:currentChildPageName change:appointmentExternalId', function() {
      // Prevent fetching 2x when wizard initially loads the first page.
      if (this.get('currentPageName')) {
        this.fetchWizardConfig();
      }
    });

    //   The second step is updating the state of the wizard views based on new the data from the server.
    this.on('change:wizardData change:questionnaireWizardData change:formWizardData', this._updateWizardData);

    //   The third step is updating the current state of the wizard views to match the context of the application.
    this.on('change:currentPageName', function() {
      this.wizardNavView.navigateToWizardPage(this.get('currentPageName'));
    });
    this.on('change:currentPageName change:currentChildPageName change:questionnaireName change:formName change:appointmentExternalId', function() {
      this.wizardButtonsView.navigateToWizardPage(this.toJSON());
    });

    this.fetchWizardConfig();
  },

  /**
   * Set the data from the configuration on the server on the managed views of this service and on this service's properties.
   * @method _updateWizardData
   * @private
   */
  _updateWizardData: function() {
    var wizardData = this.get('wizardData');
    this.wizardNavView.setWizardData(wizardData);
    this.wizardButtonsView.setWizardData(wizardData, this.get('questionnaireWizardData'), this.get('formWizardData'));
    wizardUtils.savePreRegWizardAppointmentExternalIds(this._regDate, wizardData.appointmentExternalIds);
  },

  /**
   * Generate the identifier for the current wizard (patient id, appt id, reg date).
   * @method _getWizardId
   * @return {{patientVecnaId: *, appointmentExternalId: *, regDate: *}}
   * @private
   */
  _getWizardId: function() {
    return {
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._appointmentExternalId,
      regDate: this._regDate
    };
  }
});

/**
 * Class level function that retrieves a wizard widget based on patient vecna id and appointment external id.
 * This will create a wizard widget if one doesn't already exist.
 *
 * @method getWizardWidget
 * @param wizardId {Object}
 *   @param wizardId.patientVecnaId {String} the vecna id of the patient that the wizard is for.
 *   @param wizardId.appointmentExternalId {String} the external id of the appointment that the wizard is for.  Only used if regDate is not set.
 *   @param wizardId.regDate {String} the date to perform multi-appointment register on.  If this is set then appointmentExternalId is ignored.
 * @return {WizardWidget} an instance of the wizard widget for the given patientVecnaId and appointmentExternalId
 */
function getWizardWidget(wizardId) {
  return new WizardWidget({
    patientVecnaId: wizardId.patientVecnaId,
    appointmentExternalId: wizardId.appointmentExternalId,
    regDate: wizardId.regDate,
    navViewTemplate: preRegNavTemplate
  });
}

WizardWidget.NAVIGATION_DIRECTIONS = WizardButtonsView.NAVIGATION_DIRECTIONS;
WizardWidget.PAGE_NAMES = WizardButtonsView.PAGE_NAMES;

// Need to override the default hash of memoize to include all arguments.  String concatenation should be sufficient.
function patientVecnaIdAndAppointmentExternalIdHash(wizardId) {
  if (wizardId.regDate) {
    var selectedAppointmentExternalIds = wizardUtils.getSelectedPreRegWizardAppointmentExternalIds(null, wizardId.regDate);
    return wizardId.patientVecnaId + wizardId.regDate + selectedAppointmentExternalIds.toString();
  }
  return wizardId.patientVecnaId + wizardId.appointmentExternalId;
}
// memoize the function so that it caches based on the arguments.
WizardWidget.getWizardWidget = _.memoize(getWizardWidget, patientVecnaIdAndAppointmentExternalIdHash);

module.exports = WizardWidget;
